export type ToolKey = 'chatgpt' | 'claude' | 'gemini';

export interface ToolMeta {
  key: ToolKey;
  name: string;
  vendor: string;
  tagline: string;
  strengths: string[];
  promptingTips: string[];
  url: string;
  systemHint: string;
  accent: string;
}

export const TOOLS: Record<ToolKey, ToolMeta> = {
  chatgpt: {
    key: 'chatgpt',
    name: 'ChatGPT',
    vendor: 'OpenAI',
    tagline: 'The most widely-used AI assistant, great for everyday work and custom GPTs.',
    strengths: ['Custom GPTs and projects', 'Strong tool use', 'Wide integrations'],
    promptingTips: [
      'Set context in a custom instruction or system message at the top.',
      'Use numbered steps when you want a sequence of outputs.',
      'Lean on GPTs for repeated workflows.',
    ],
    url: 'https://chat.openai.com/',
    systemHint:
      'Format the answer the way ChatGPT typically does: clear headings, numbered or bulleted steps, code fenced with triple backticks, and a brief summary at the end.',
    accent: 'emerald',
  },
  claude: {
    key: 'claude',
    name: 'Claude',
    vendor: 'Anthropic',
    tagline: 'Best in class for long, careful writing and structured reasoning.',
    strengths: ['Long-context analysis', 'Document review', 'Faithful instruction following'],
    promptingTips: [
      'Wrap context in XML tags like <context> and <task> for clarity.',
      'Ask Claude to think step-by-step before answering.',
      'Provide examples — Claude follows patterns precisely.',
    ],
    url: 'https://claude.ai/',
    systemHint:
      'Format the answer the way Claude typically does: explicit XML-style tags around major sections (e.g. <analysis>, <answer>), thoughtful step-by-step reasoning before conclusions, and precise, plain prose.',
    accent: 'amber',
  },
  gemini: {
    key: 'gemini',
    name: 'Gemini',
    vendor: 'Google',
    tagline: 'Deeply integrated with Google Workspace and great for research with grounding.',
    strengths: ['Workspace integration', 'Real-time search grounding', 'Multi-modal inputs'],
    promptingTips: [
      'Tell Gemini whether to ground in Google Search.',
      'Ask for citations and links when researching.',
      'Use it inside Docs and Gmail for in-place drafts.',
    ],
    url: 'https://gemini.google.com/',
    systemHint:
      'Format the answer the way Gemini typically does: scannable headings, short paragraphs, frequent bullet lists, and an explicit "Sources" or "Next steps" section at the end.',
    accent: 'sky',
  },
};

export const TOOL_LIST: ToolMeta[] = [TOOLS.chatgpt, TOOLS.claude, TOOLS.gemini];

export function getTool(key: string | null | undefined): ToolMeta {
  if (key && (key === 'chatgpt' || key === 'claude' || key === 'gemini')) return TOOLS[key];
  return TOOLS.chatgpt;
}

export function deepLinkForTool(key: ToolKey): string {
  return TOOLS[key].url;
}
